import { useCallback, useRef } from 'react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

import { useLenis } from '@/contexts/lenis'
import { useMotionTouchMedia } from '@/hooks'
import { useLoaderStore } from '@/store/loader'

import {
  createLinesMotion,
  createMaskMotion,
  createWordsMotion,
  getScrollTriggerOptions,
  Motion
} from './shared'

export const useSingleTextMotion = () => {
  const motionRef = useRef<HTMLDivElement>(null)
  const scrollTrigger = useRef<ScrollTrigger>(null!)
  const fontsReady = useLoaderStore((state) => state.fontsReady)
  const { lenis } = useLenis()

  const titleMotion = useRef<Motion | null>(null)
  const pretitleMotion = useRef<Motion | null>(null)
  const paragraphMotion = useRef<Motion | null>(null)

  const revertSplitText = useCallback(() => {
    titleMotion.current?.revert()
    pretitleMotion.current?.revert()
    paragraphMotion.current?.revert()
  }, [])

  /**
   * reveal setup
   */
  useMotionTouchMedia(
    {
      pointer({ selector }) {
        if (!fontsReady || !lenis) return

        const title = selector?.('[data-motion-title]')[0]
        const pretitle = selector?.('[data-motion-pretitle]')[0]
        const paragraph = selector?.('[data-motion-paragraph]')[0]

        titleMotion.current = title ? createWordsMotion(title) : null
        pretitleMotion.current = pretitle ? createMaskMotion(pretitle) : null
        paragraphMotion.current = paragraph ? createLinesMotion(paragraph) : null

        const animation = gsap.timeline({ onComplete: revertSplitText })
        titleMotion.current && animation.add(titleMotion.current.timeline, 0)
        pretitleMotion.current && animation.add(pretitleMotion.current.timeline, 0.1)
        paragraphMotion.current && animation.add(paragraphMotion.current.timeline, 0.2)

        scrollTrigger.current = ScrollTrigger.create({
          ...getScrollTriggerOptions({
            trigger: motionRef.current,
            animation
          })
        })
      },

      revert() {
        scrollTrigger.current?.kill()
        revertSplitText()
      }
    },
    { scope: motionRef, dependencies: [fontsReady, lenis] }
  )

  return { motionRef }
}
